import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";
import { logger } from "../lib/logger";

// =============================================================================
// useDismissedLeaderboard
// =============================================================================
// Reads the org-wide "who dismisses the most" leaderboard from the
// get_dismissed_leaderboard RPC (SECURITY DEFINER, server-side aggregate).
// Powers the stats strip on the Dismissed Leads panel — Setter Performance tab.
//
// WHY A SERVER-SIDE AGGREGATE (not useDismissedConversations):
// - useDismissedConversations reads the base table directly and is capped at
//   PGRST_MAX_ROWS (1000). Rayston already has 2670 dismissed all-time, so a
//   client-side count off that hook silently undercounts. This RPC returns the
//   TRUE total plus both grouped cuts (by dismisser, by assignee) computed
//   server-side over the full population.
//
// NO CACHE: plain fetch on every dep change. The leaderboard total must never
// disagree with a just-fired dismiss — the owning tab calls `refresh()` after
// both dismiss paths (single-dismiss + bulk-dismiss from the grid modal).
// =============================================================================

export interface DismissedLeaderboardDismisser {
  id: string;
  name: string;
  count: number;
}

export interface DismissedLeaderboardAssignee {
  ghlUserId: string | null;
  count: number;
}

interface DismissedLeaderboardResponse {
  total: number;
  by_dismisser: { id: string; name: string; count: number }[];
  by_assignee: { ghl_user_id: string | null; count: number }[];
}

interface Params {
  organizationId: string | null;
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
}

export function useDismissedLeaderboard({ organizationId, dateRange, timezone }: Params) {
  const [total, setTotal] = useState(0);
  const [byDismisser, setByDismisser] = useState<DismissedLeaderboardDismisser[]>([]);
  const [byAssignee, setByAssignee] = useState<DismissedLeaderboardAssignee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const genRef = useRef(0);

  const startISO = dateRange.start ? toOrgStartISO(dateRange.start, timezone) : null;
  const endISO = dateRange.end ? toOrgEndISO(dateRange.end, timezone) : null;

  const fetchLeaderboard = useCallback(async () => {
    if (!supabase || !organizationId || !startISO || !endISO) {
      setTotal(0);
      setByDismisser([]);
      setByAssignee([]);
      return;
    }

    const myGen = ++genRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: qErr } = await supabase.rpc("get_dismissed_leaderboard", {
        p_organization_id: organizationId,
        p_start_ts: startISO,
        p_end_ts: endISO,
      });

      if (genRef.current !== myGen) return; // a newer fetch superseded this one
      if (qErr) throw qErr;

      const payload = data as DismissedLeaderboardResponse | null;

      setTotal(payload?.total ?? 0);
      setByDismisser(payload?.by_dismisser ?? []);
      setByAssignee(
        (payload?.by_assignee ?? []).map((row) => ({
          ghlUserId: row.ghl_user_id,
          count: row.count,
        }))
      );
    } catch (e) {
      if (genRef.current !== myGen) return;
      logger.warn("useDismissedLeaderboard fetch failed", e);
      setError(e instanceof Error ? e.message : "Failed to load dismissed leaderboard");
    } finally {
      if (genRef.current === myGen) setIsLoading(false);
    }
  }, [organizationId, startISO, endISO]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return {
    total,
    byDismisser,
    byAssignee,
    isLoading,
    error,
    refresh: fetchLeaderboard,
  };
}
